import { useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate, useParams } from '@/hooks/useNavigate.ts';
import { useApplyJobDetails, useApplyJobSubmission } from '@/api/services.ts';
import { Input } from '@/components/Input.tsx';
import { Button } from '@/components/ui/button.tsx';
import { z } from 'zod';
import {
  ControllerFieldState,
  useForm,
  type SubmitErrorHandler,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { ArrowRight } from 'lucide-react';
import { usePageTitle } from '@/hooks/usePageTitle.ts';
import { cn } from '@/lib/utils.ts';
import { Trans, useTranslation } from 'react-i18next';
import { useAppLanguage } from '@/hooks/useAppLanguage.ts';
import { useBrandColors } from '@/hooks/useBrandColors.ts';
import { useEffectiveLanguage } from '@/hooks/useEffectiveLanguage.ts';
import { isInAppBrowser } from '@/pages/Answers/InAppBrowserBlock.tsx';
import { actionColor } from '@/lib/brandColor.ts';
import { ApplyFlowShell } from './components/ApplyFlowShell';
import { StepProgress } from './components/StepProgress';
import { normalizeLanguages } from '@/lib/language';
import { PositionField } from './components/PositionField';
import { ContinueGate } from './components/ContinueGate';
import { buildPositionOptions, parseSelection } from './positionOptions';
import { containsCyrillic, handoffPath } from './applyJobUtils';
import {
  isDuplicateApplicationError,
  mergeUnfinished,
  parseDuplicateApplication,
} from './duplicateApplication';
import type { UnfinishedInterview } from '@/api/types';
import { toast } from 'sonner';

type ApplyFormValues = {
  position_id: string;
  first_name: string;
  last_name: string;
  phone_number: string;
};

// Schema / visual order of the fields — used to pick the first invalid one so a
// failed submit scrolls to (and focuses) it. RHF's own shouldFocusError can't
// reach the Position Select (no registered ref) or the masked phone input, so we
// drive scroll + focus by DOM id here. The ids are `position_id` on the
// SelectTrigger and `first_name`/`last_name`/`phone_number` on the inputs.
const FIELD_ORDER: (keyof ApplyFormValues)[] = [
  'position_id',
  'first_name',
  'last_name',
  'phone_number',
];

const focusFirstInvalidField: SubmitErrorHandler<ApplyFormValues> = (errors) => {
  const firstInvalid = FIELD_ORDER.find((name) => errors[name]);
  if (!firstInvalid) return;
  const el = document.getElementById(firstInvalid);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  // preventScroll so the focus jump doesn't fight the smooth scrollIntoView.
  el.focus({ preventScroll: true });
};

const useInterviewSubmissionForm = () => {
  const { t } = useTranslation('applyJob');
  const latinName = (requiredMessage: string) =>
    z
      .string()
      .min(1, requiredMessage)
      .refine((value) => !containsCyrillic(value), t('validation.latin_only'));
  const schema = z.object({
    position_id: z.string().min(1, t('validation.position_required')),
    first_name: latinName(t('validation.first_name_required')),
    last_name: latinName(t('validation.last_name_required')),
    phone_number: z.string().min(1, t('validation.phone_number_required')),
  });
  return useForm<ApplyFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    shouldFocusError: false, // handled manually in focusFirstInvalidField
    defaultValues: {
      position_id: '',
      first_name: '',
      last_name: '',
      phone_number: '',
    },
  });
};

const ApplyJob = () => {
  const [language, setLanguage] = useAppLanguage();

  const navigate = useNavigate();
  const { t } = useTranslation('applyJob');
  const params = useParams<{ property_id: string }>();
  const {
    data: jobDetails,
    isLoading,
    isError,
    refetch: refetchJobDetails,
  } = useApplyJobDetails(params.property_id || '', language);
  const { availableLanguages, effectiveLanguage } = useEffectiveLanguage(
    language,
    jobDetails?.positions
  );
  const normalizedLanguages = normalizeLanguages(availableLanguages);
  const { mutateAsync: triggerSubmissionApply, isLoading: isSubmitting } =
    useApplyJobSubmission(params.property_id || '');

  const { backgroundColor, textColor } = useBrandColors(jobDetails);
  const hookForm = useInterviewSubmissionForm();

  // A 409 on submit means this visitor already has an interview open for the
  // position they just picked. Folding it in here — rather than handling it as
  // its own screen — means the gate item, the removal of that role from the
  // picker, and `canApplyElsewhere` all fall out of machinery that already
  // exists. Cleared by a refetch that reports the same interview itself.
  const [duplicate, setDuplicate] = useState<UnfinishedInterview | null>(null);

  const options = useMemo(
    () =>
      buildPositionOptions(
        jobDetails?.positions,
        mergeUnfinished(jobDetails?.unfinished_interview, duplicate)
      ),
    [jobDetails, duplicate]
  );
  // Rows the picker actually renders. Not `positions.length`: a role already in
  // progress is removed from `available`, and an in-progress interview whose
  // role has closed has no position behind it at all.
  const rowCount = options.available.length + options.inProgress.length;

  // `buildPositionOptions` matches on `position_id`, so an entry arriving
  // without one silently fails to remove its position from `available` — the
  // picker reverts to pre-feature behaviour and a duplicate application
  // becomes possible again. Nothing breaks loudly, so report it: this is the
  // shape of a backend that hasn't shipped the field yet.
  useEffect(() => {
    const missingId = jobDetails?.unfinished_interview?.some(
      (item) => item.invitation_key && !Number.isFinite(item.position_id)
    );
    if (missingId) {
      Sentry.captureMessage(
        'unfinished_interview item missing position_id — its position cannot be removed from the available list',
        { level: 'warning', extra: { property_id: params.property_id } }
      );
    }
  }, [jobDetails, params.property_id]);

  const positionIdValue = hookForm.watch('position_id');
  const selection = parseSelection(positionIdValue);
  const continueKey =
    selection?.kind === 'continue' ? selection.invitationKey : null;

  // A held `position_id` can be orphaned by a fresh `options` (e.g. a language
  // switch, or the role the applicant picked moving into `unfinished_interview`
  // on a refetch): the Radix trigger has a value nothing matches, which
  // renders blank instead of the placeholder, while the CTA and the eventual
  // submit still carry the stale id. Clearing it here restores the
  // placeholder and the START INTERVIEW submit CTA — the correct resting
  // state — the moment the value stops being a real option.
  useEffect(() => {
    if (!positionIdValue) return;
    const stillValid = [...options.inProgress, ...options.available].some(
      (option) => option.value === positionIdValue
    );
    if (!stillValid) {
      hookForm.setValue('position_id', '');
    }
  }, [options, positionIdValue, hookForm]);

  const goToInterview = (invitationKey: string) => {
    navigate(
      handoffPath(invitationKey, params.property_id || '', isInAppBrowser())
    );
  };

  // The gate is shown before the form whenever the session has unfinished
  // interviews, so continuing one is the first thing offered rather than
  // something to notice inside the picker. Dismissal is deliberately not
  // persisted: it is a reminder rather than a decision, it costs one tap to
  // clear, and storage would be one more thing to keep in sync. A reload shows
  // it again.
  const [gateResolved, setGateResolved] = useState(false);

  // Latch the decision to the first load with data. The gate is an entry-point
  // interruption; a later refetch (a language switch, a window-focus refetch)
  // that newly reports an unfinished interview must not yank a half-filled
  // form out from under the applicant and discard what they typed.
  useEffect(() => {
    if (jobDetails && options.inProgress.length === 0) {
      setGateResolved(true);
    }
  }, [jobDetails, options.inProgress.length]);

  const showGate = !gateResolved && options.inProgress.length > 0;

  const onSubmit = async (data: ApplyFormValues) => {
    // Defense in depth. No submit-capable control is reachable while a continue
    // is selected (the CTA is type="button" and the person fields are
    // disabled), so this is unreachable today — it exists so a future refactor
    // that reintroduces one cannot POST `position_id: "continue:<key>"`.
    if (parseSelection(data.position_id)?.kind === 'continue') return;
    try {
      const response = await triggerSubmissionApply(data);
      goToInterview(response.invitation_key);
    } catch (error) {
      // A duplicate application isn't a form problem — there is nothing to fix
      // by editing a field. Hand the screen to the gate so continuing the
      // interview that already exists is the offered action.
      if (isDuplicateApplicationError(error)) {
        toast.error(t('already_applied_toast'), { duration: 6000 });

        const local = parseDuplicateApplication(
          error,
          data.position_id,
          jobDetails?.positions
        );
        // The backend may only report the interview against this session from
        // now on, so ask again even when the 409 told us nothing useful.
        const refreshed = await refetchJobDetails().catch(() => null);
        const fromServer = refreshed?.data?.unfinished_interview ?? [];

        // Order matters: the refetch is awaited *before* the flip so that
        // `setDuplicate` and `setGateResolved` batch into a single render where
        // `options.inProgress` is already non-empty. Flipping first would let
        // the latch effect below immediately re-close the gate.
        if (local || fromServer.some((item) => item.invitation_key)) {
          if (local) setDuplicate(local);
          setGateResolved(false);
        }
        // Nothing continuable from either source: the applicant keeps the form
        // they filled in and the toast is the whole story.
        return;
      }

      const errors = JSON.parse((error as Error).message);
      for (const field in errors) {
        if (field in data) {
          hookForm.setError(field as keyof typeof data, {
            type: 'custom',
            message: errors[field][0],
          });
        }
      }
    }
  };

  usePageTitle(jobDetails?.client_name, 'apply');

  if (isLoading) {
    return (
      <div className="grid min-h-[100dvh] place-items-center bg-[#f4f4f5] text-[#9aa0a8]">
        <div className="size-8 animate-spin rounded-full border-2 border-current border-t-transparent" />
      </div>
    );
  }

  // Before `noData` on purpose. A role can close while its interview is still
  // unfinished, which yields no selectable rows alongside a non-empty
  // `unfinished_interview`. Evaluating `noData` first would render "No open
  // roles right now" over a continuable interview and bury it.
  if (showGate && jobDetails) {
    return (
      <ContinueGate
        items={options.inProgress}
        bgColor={backgroundColor}
        inkColor={textColor}
        logoUrl={jobDetails.logo_url ?? ''}
        brandName={jobDetails.client_name}
        language={effectiveLanguage}
        availableLanguages={normalizedLanguages}
        onLanguageChange={setLanguage}
        onContinue={goToInterview}
        onDismiss={() => setGateResolved(true)}
        // Dismissing with nothing left to apply for would strand the applicant
        // on an empty form, so the escape hatch is hidden rather than offered
        // as a dead end.
        canApplyElsewhere={options.available.length > 0}
      />
    );
  }

  const noData =
    isError || !jobDetails || rowCount === 0
      ? { title: t('no_positions_title'), body: t('no_positions_body') }
      : undefined;

  const inputClasses =
    'h-12 rounded-md border border-[#e3e4e8] font-medium text-[#16171a] ' +
    // Clear the pinned brand hero (+ room for the field label) when a failed
    // submit scrolls this field into view. --hero-expanded is inherited from
    // .apply-flow-shell and is already responsive.
    'scroll-mt-[calc(var(--hero-expanded,168px)_+_2rem)]';
  const labelClasses = 'text-sm font-medium text-[#54585f]';

  return (
    <ApplyFlowShell
      bgColor={backgroundColor}
      inkColor={textColor}
      logoUrl={jobDetails?.logo_url ?? ''}
      brandName={jobDetails?.client_name}
      welcomeTitle={
        <Trans
          i18nKey="applyJob:welcome_title"
          components={{
            br: <br />,
          }}
        />
      }
      language={effectiveLanguage}
      availableLanguages={normalizedLanguages}
      onLanguageChange={setLanguage}
      onSubmit={hookForm.handleSubmit(onSubmit, focusFirstInvalidField)}
      noData={noData}
      footer={
        <Button
          variant="brand"
          className="w-full gap-2 rounded-xl max-w-md short:h-[52px]"
          size="lg"
          disabled={isSubmitting}
          // A continue is a navigation, not a submission: as `type="submit"` it
          // would run handleSubmit and the untouched name/phone fields would
          // block it. `type="button"` skips RHF entirely and leaves whatever
          // the applicant typed alone.
          type={continueKey ? 'button' : 'submit'}
          onClick={continueKey ? () => goToInterview(continueKey) : undefined}
        >
          {continueKey
            ? t('continue_interview_button')
            : t('start_interview_button')}
          <ArrowRight className="size-4" aria-hidden />
        </Button>
      }
      lockHero="full"
    >
      {jobDetails && (
        <>
          <StepProgress
            step={2}
            brandColor={actionColor(backgroundColor)}
            label={t('step_progress', { step: 2, total: 3 })}
            lastStepLabel={t('interview_step')}
          />
          <Form {...hookForm}>
            <div className="mt-4 flex flex-col gap-3 short:mt-3 short:gap-1.5">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-[#8a8f98] short:hidden">
                {t('welcome_message')}
              </div>

              <FormField
                control={hookForm.control}
                name="position_id"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel className={labelClasses} errorClassName="">
                      {t('position_label')}
                    </FormLabel>
                    <FormControl>
                      <PositionField
                        options={options}
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                          // Errors on the person fields become unreachable once
                          // those fields go disabled — clear them so red
                          // styling doesn't sit behind dead inputs.
                          if (parseSelection(value)?.kind === 'continue') {
                            hookForm.clearErrors([
                              'first_name',
                              'last_name',
                              'phone_number',
                            ]);
                          }
                        }}
                        accent={actionColor(backgroundColor)}
                        placeholder={t('positions_available', {
                          count: rowCount,
                        })}
                        disabled={isSubmitting || rowCount === 0}
                        hasError={!!fieldState.error}
                        triggerClassName={cn(
                          inputClasses,
                          getInputErrorClasses(fieldState)
                        )}
                      />
                    </FormControl>
                    <FormMessage className="" />
                  </FormItem>
                )}
              />

              <FormField
                control={hookForm.control}
                name="first_name"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel
                      htmlFor="first_name"
                      className={labelClasses}
                      errorClassName=""
                    >
                      {t('first_name_label')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="first_name"
                        type="text"
                        className={cn(
                          inputClasses,
                          getInputErrorClasses(fieldState)
                        )}
                        {...field}
                        // Overrides must follow the spread: the display is
                        // blanked while continuing, but RHF state is left
                        // alone so anything typed returns on switching back.
                        value={continueKey ? '' : field.value}
                        disabled={!!continueKey}
                        placeholder={continueKey ? t('already_filled') : undefined}
                      />
                    </FormControl>
                    <FormMessage className="" />
                  </FormItem>
                )}
              />

              <FormField
                control={hookForm.control}
                name="last_name"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel
                      htmlFor="last_name"
                      className={labelClasses}
                      errorClassName=""
                    >
                      {t('last_name_label')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="last_name"
                        type="text"
                        className={cn(
                          inputClasses,
                          getInputErrorClasses(fieldState)
                        )}
                        {...field}
                        // Overrides must follow the spread: the display is
                        // blanked while continuing, but RHF state is left
                        // alone so anything typed returns on switching back.
                        value={continueKey ? '' : field.value}
                        disabled={!!continueKey}
                        placeholder={continueKey ? t('already_filled') : undefined}
                      />
                    </FormControl>
                    <FormMessage className="" />
                  </FormItem>
                )}
              />

              <FormField
                control={hookForm.control}
                name="phone_number"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel
                      htmlFor="phone_number"
                      className={labelClasses}
                      errorClassName=""
                    >
                      {t('phone_number_label')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="phone_number"
                        type="text"
                        // Drop the mask while continuing: it takes over the ref
                        // and injects its own placeholder, overwriting ours.
                        kind={continueKey ? undefined : 'phone_number'}
                        className={cn(
                          inputClasses,
                          getInputErrorClasses(fieldState)
                        )}
                        {...field}
                        value={continueKey ? '' : field.value}
                        disabled={!!continueKey}
                        placeholder={
                          continueKey
                            ? t('already_filled')
                            : t('phone_number_placeholder')
                        }
                      />
                    </FormControl>
                    <FormMessage className="" />
                  </FormItem>
                )}
              />
            </div>
          </Form>
        </>
      )}
    </ApplyFlowShell>
  );
};

function getInputErrorClasses(fieldState: ControllerFieldState) {
  if (fieldState.error) {
    return 'bg-red-100';
  }

  return '';
}

export default ApplyJob;
